import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Info, User, Building2, CreditCard, Search, Shield, FileText, Trash2, ChevronRight } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { AdSlot } from "../components/AdSense";

const CATS = [
  { key: "general", icon: Info },
  { key: "candidates", icon: User },
  { key: "employers", icon: Building2 },
  { key: "plans", icon: CreditCard },
];

const LINKS = [
  { key: "privacy", icon: Shield, to: "/privacy" },
  { key: "terms", icon: FileText, to: "/terms" },
  { key: "deletion", icon: Trash2, to: "/data-deletion" },
];

export default function Help() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");

  const data = useMemo(() => CATS.map((c) => ({
    ...c,
    label: t(`help.cats.${c.key}`),
    items: t(`help.faqs.${c.key}`, { returnObjects: true }) || [],
  })), [t]);

  const query = q.trim().toLowerCase();
  const sections = data
    .filter((s) => cat === "all" || s.key === cat)
    .map((s) => ({
      ...s,
      items: query ? s.items.filter((it) => (`${it.q} ${it.a}`).toLowerCase().includes(query)) : s.items,
    }))
    .filter((s) => s.items.length > 0);

  const showLinks = !query;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="help-page">
      <h1 className="font-heading text-3xl sm:text-4xl font-bold text-stone-text">{t("help.title")}</h1>
      <p className="text-stone-sub mt-2 mb-6">{t("help.subtitle")}</p>

      <div className="relative mb-4">
        <Search className="w-5 h-5 text-stone-muted absolute left-4 top-1/2 -translate-y-1/2" />
        <input
          data-testid="help-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("help.searchPlaceholder")}
          className="w-full pl-12 pr-4 py-3 rounded-2xl border border-stone-border bg-white focus:outline-none focus:ring-2 focus:ring-brand"
        />
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 mb-6" data-testid="help-cats">
        <button
          data-testid="help-cat-all"
          onClick={() => setCat("all")}
          className={`shrink-0 px-5 py-2 rounded-full text-sm font-semibold border transition-colors ${cat === "all" ? "bg-brand text-white border-brand" : "bg-white text-stone-sub border-stone-border hover:border-brand"}`}
        >
          {t("help.all")}
        </button>
        {CATS.map((c) => (
          <button
            key={c.key}
            data-testid={`help-cat-${c.key}`}
            onClick={() => setCat(c.key)}
            className={`shrink-0 px-5 py-2 rounded-full text-sm font-semibold border whitespace-nowrap transition-colors ${cat === c.key ? "bg-brand text-white border-brand" : "bg-white text-stone-sub border-stone-border hover:border-brand"}`}
          >
            {t(`help.cats.${c.key}`)}
          </button>
        ))}
      </div>

      {sections.length === 0 ? (
        <div className="py-16 text-center text-stone-muted bg-white rounded-2xl border border-stone-border" data-testid="help-no-results">
          {t("help.noResults")}
        </div>
      ) : (
        <div className="space-y-8">
          {sections.map((s) => {
            const Icon = s.icon;
            return (
              <section key={s.key} data-testid={`help-section-${s.key}`}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-full bg-brand-soft flex items-center justify-center">
                    <Icon className="w-4 h-4 text-brand" />
                  </div>
                  <h2 className="font-heading text-lg font-bold text-stone-text">{s.label}</h2>
                </div>
                <Accordion type="single" collapsible className="space-y-3">
                  {s.items.map((it, i) => (
                    <AccordionItem
                      key={i}
                      value={`${s.key}-${i}`}
                      data-testid={`faq-item-${s.key}-${i}`}
                      className="bg-white rounded-2xl border border-stone-border shadow-sm px-4"
                    >
                      <AccordionTrigger className="font-heading text-left font-semibold text-stone-text hover:no-underline">
                        {it.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-stone-sub leading-relaxed">{it.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </section>
            );
          })}
        </div>
      )}

      {showLinks && (
        <div className="space-y-3 mt-8" data-testid="help-links">
          {LINKS.map((l) => {
            const Icon = l.icon;
            return (
              <button
                key={l.key}
                data-testid={`help-link-${l.key}`}
                onClick={() => navigate(l.to)}
                className="w-full flex items-center justify-between gap-3 bg-white rounded-2xl border border-brand/30 shadow-sm px-4 py-4 text-left hover:border-brand transition-colors"
              >
                <span className="flex items-center gap-3">
                  <Icon className="w-5 h-5 text-brand shrink-0" />
                  <span className="font-heading font-semibold text-stone-text">{t(`help.links.${l.key}`)}</span>
                </span>
                <ChevronRight className="w-5 h-5 text-stone-muted shrink-0" />
              </button>
            );
          })}
        </div>
      )}

      <AdSlot className="mt-8" />

      <p className="text-center text-sm text-stone-muted mt-8">{t("help.contact")}</p>
    </div>
  );
}
